/**
 * StrategyBuilder - Main component for building and backtesting strategies
 * 
 * UI follows the design mockup:
 * - Right sidebar panel layout
 * - Strategy name, asset, timeframe inputs
 * - Buy/Sell conditions sections
 * - Build Strategy button
 * - Backtest results display
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Input, 
  Select, 
  Button, 
  Space, 
  Typography, 
  Divider, 
  Tag, 
  message, 
  Spin,
  InputNumber,
  Switch,
  Tooltip,
  Modal,
  Skeleton,
  Segmented
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  ThunderboltOutlined,
  FilterOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  SendOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useAppTheme } from '../../theme/ThemeProvider';
import { SavedSignal, SellRule, BacktestResults, Strategy, AIAnalysisMessage, TradingMode, PositionDirection, FuturesSettings } from './types';
import SignalSelectorModal from './SignalSelectorModal';
import BacktestResultsComponent from './BacktestResults';
import { runBacktest, saveStrategy, analyzeStrategy } from '../../services/strategyService';
import { fetchHistoricalFromBackend } from '../../services';
import { formatTimestamp } from '../../utils/timeUtils';
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts';

const { Title, Text } = Typography;

// Trade Chart Component with entry/exit markers
interface TradeChartProps {
  data: any[];
  entryTime: number;
  exitTime: number;
  entryPrice: number;
  exitPrice: number;
  theme: any;
}

const TradeChartComponent: React.FC<TradeChartProps> = ({ 
  data, entryTime, exitTime, entryPrice, exitPrice, theme 
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current || data.length === 0) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 350,
      layout: {
        background: { color: '#1a1a2e' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(42, 46, 57, 0.5)' },
        horzLines: { color: 'rgba(42, 46, 57, 0.5)' },
      },
      crosshair: {
        mode: 1,
      },
      timeScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
      },
    });

    chartRef.current = chart;

    // Add candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderDownColor: '#ef5350',
      borderUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      wickUpColor: '#26a69a',
    });

    candleSeries.setData(data);

    // Add entry marker
    candleSeries.setMarkers([
      {
        time: entryTime as any,
        position: 'belowBar',
        color: '#1890ff',
        shape: 'arrowUp',
        text: `Entry $${entryPrice?.toLocaleString()}`,
      },
      {
        time: exitTime as any,
        position: 'aboveBar',
        color: exitPrice >= entryPrice ? '#52c41a' : '#ff4d4f',
        shape: 'arrowDown',
        text: `Exit $${exitPrice?.toLocaleString()}`,
      },
    ]);

    // Fit content
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, entryTime, exitTime, entryPrice, exitPrice]);

  return <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />;
};

interface StrategyBuilderProps {
  onBack?: () => void;
  editingStrategy?: Strategy | null;
  onSaveComplete?: () => void;
  showBackButton?: boolean;
}

const StrategyBuilder: React.FC<StrategyBuilderProps> = ({ 
  onBack, 
  editingStrategy,
  onSaveComplete,
  showBackButton = false
}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();

  // Form state
  const [asset, setAsset] = useState('BTCUSDT');
  const [timeframe, setTimeframe] = useState('1h');
  
  // Signal selections
  const [buySignal, setBuySignal] = useState<SavedSignal | null>(null);
  const [sellSignal, setSellSignal] = useState<SavedSignal | null>(null);
  
  // Sell rule configuration
  const [sellRuleType, setSellRuleType] = useState<'fixed_rule' | 'signal'>('fixed_rule');
  const [takeProfitPct, setTakeProfitPct] = useState<number | null>(5);
  const [stopLossPct, setStopLossPct] = useState<number | null>(2);
  const [maxHoldingHours, setMaxHoldingHours] = useState<number | null>(168); // Default 1 week
  const [enableTakeProfit, setEnableTakeProfit] = useState(true);
  const [enableStopLoss, setEnableStopLoss] = useState(true);
  const [enableMaxHolding, setEnableMaxHolding] = useState(true);
  
  // Futures trading configuration
  const [tradingMode, setTradingMode] = useState<'spot' | 'futures'>('spot');
  const [positionDirection, setPositionDirection] = useState<'long' | 'short'>('long');
  const [leverage, setLeverage] = useState<number>(1);
  
  // UI state
  const [showBuySignalModal, setShowBuySignalModal] = useState(false);
  const [showSellSignalModal, setShowSellSignalModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [backtestResults, setBacktestResults] = useState<BacktestResults | null>(null);
  const [viewMode, setViewMode] = useState<'build' | 'performance'>('build');
  
  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveStrategyName, setSaveStrategyName] = useState('');
  const [saveStrategyDescription, setSaveStrategyDescription] = useState('');
  
  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [chatHistory, setChatHistory] = useState<AIAnalysisMessage[]>([]);
  const [followUpInput, setFollowUpInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const analysisContainerRef = useRef<HTMLDivElement>(null);
  const followUpInputRef = useRef<any>(null);
  
  // Trade chart modal state
  const [selectedTrade, setSelectedTrade] = useState<any>(null);
  const [showTradeChartModal, setShowTradeChartModal] = useState(false);
  
  // Before/After split view mode
  const [splitViewMode, setSplitViewMode] = useState<'all' | 'before' | 'after'>('all');
  const [tradeChartData, setTradeChartData] = useState<any[]>([]);
  const [tradeChartLoading, setTradeChartLoading] = useState(false);
  const tradeChartContainerRef = useRef<HTMLDivElement>(null);
  const tradeChartInstanceRef = useRef<IChartApi | null>(null);
  const tradeCandleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);

  // Handle viewing a trade chart
  const handleViewTrade = async (trade: any) => {
    setSelectedTrade(trade);
    setShowTradeChartModal(true);
    setTradeChartLoading(true);
    
    try {
      const entryTimeSec = Math.floor(trade.entry_time / 1000);
      const exitTimeSec = Math.floor(trade.exit_time / 1000);
      
      const intervalHours = timeframe === '1h' ? 1 : timeframe === '4h' ? 4 : timeframe === '1d' ? 24 : 1;
      const paddingCandles = 15;
      
      const { data } = await fetchHistoricalFromBackend(asset, timeframe, 10000);
      
      if (data && data.length > 0) {
        const startTime = entryTimeSec - (paddingCandles * intervalHours * 3600);
        const endTime = exitTimeSec + (paddingCandles * intervalHours * 3600);
        
        const filteredData = data.filter((d: any) => d.time >= startTime && d.time <= endTime);
        
        if (filteredData.length > 0) {
          setTradeChartData(filteredData);
        } else {
          setTradeChartData([]);
        }
      }
    } catch (error) {
      console.error('[TradeChart] Error fetching data:', error);
      setTradeChartData([]);
    } finally {
      setTradeChartLoading(false);
    }
  };

  // Initialize from editing strategy
  useEffect(() => {
    if (editingStrategy) {
      setSaveStrategyName(editingStrategy.name);
      setAsset(editingStrategy.symbol);
      setTimeframe(editingStrategy.timeframe);
      
      if (editingStrategy.sell_rule) {
        setSellRuleType(editingStrategy.sell_rule.type);
        setTakeProfitPct(editingStrategy.sell_rule.take_profit_pct || null);
        setStopLossPct(editingStrategy.sell_rule.stop_loss_pct || null);
      }
      
      if (editingStrategy.backtest_results) {
        setBacktestResults(editingStrategy.backtest_results);
      }
    }
  }, [editingStrategy]);

  // Trigger AI analysis when entering performance mode
  useEffect(() => {
    if (viewMode === 'performance' && backtestResults && !aiAnalysis && !isAnalyzing) {
      runAIAnalysis();
    }
  }, [viewMode, backtestResults]);

  // Auto-scroll analysis container only when streaming or new messages
  const prevChatLengthRef = useRef(chatHistory.length);
  useEffect(() => {
    // Only scroll when streaming or when chat history grows
    if (analysisContainerRef.current) {
      if (isStreaming || chatHistory.length > prevChatLengthRef.current) {
        analysisContainerRef.current.scrollTop = analysisContainerRef.current.scrollHeight;
      }
    }
    prevChatLengthRef.current = chatHistory.length;
  }, [aiAnalysis, isStreaming, chatHistory.length]);

  // Run AI analysis
  const runAIAnalysis = async (userMessage?: string) => {
    if (!backtestResults) return;
    
    setIsAnalyzing(true);
    setIsStreaming(true);
    setAiAnalysis(''); // Clear for new response
    
    // If it's a follow-up, append user message to history first
    if (userMessage) {
      setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    
    try {
      let fullResponse = '';
      const generator = analyzeStrategy({
        backtest_results: backtestResults,
        strategy_config: {
          symbol: asset,
          timeframe: timeframe,
          sell_rule: buildSellRule(),
          trading_mode: tradingMode,
          futures_settings: tradingMode === 'futures' ? {
            leverage: leverage,
            direction: positionDirection
          } : undefined
        },
        buy_signal_name: buySignal?.name || '',
        message: userMessage,
        history: chatHistory,
        language: i18n.language === 'en-US' ? 'en-US' : 'zh-CN'
      });
      
      for await (const chunk of generator) {
        fullResponse += chunk;
        setAiAnalysis(fullResponse);
      }
      
      // Update chat history with AI response and clear aiAnalysis
      if (fullResponse) {
        setChatHistory(prev => [...prev, { role: 'assistant', content: fullResponse }]);
        setAiAnalysis(''); // Clear after adding to history
      }
      
    } catch (error: any) {
      console.error('[StrategyBuilder] AI analysis error:', error);
      const errorMsg = i18n.language === 'en-US' ? `Analysis failed: ${error.message}` : `分析失败: ${error.message}`;
      setChatHistory(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsAnalyzing(false);
      setIsStreaming(false);
    }
  };

  // Handle follow-up question
  const handleFollowUp = async () => {
    if (!followUpInput.trim() || isAnalyzing) return;
    
    const question = followUpInput.trim();
    setFollowUpInput('');
    await runAIAnalysis(question);
  };

  // Reset AI analysis state and go back to build mode
  const handleBackToBuild = () => {
    setViewMode('build');
    setAiAnalysis('');
    setChatHistory([]);
    setFollowUpInput('');
  };

  // Build sell rule object
  const buildSellRule = (): SellRule => {
    const baseRule = {
      take_profit_pct: enableTakeProfit ? (takeProfitPct || undefined) : undefined,
      stop_loss_pct: enableStopLoss ? (stopLossPct || undefined) : undefined,
      max_holding_hours: enableMaxHolding ? (maxHoldingHours || undefined) : undefined
    };
    
    if (sellRuleType === 'signal' && sellSignal) {
      return {
        type: 'signal',
        sell_signal_id: sellSignal.id,
        ...baseRule
      };
    }
    return {
      type: 'fixed_rule',
      ...baseRule
    };
  };

  // Run backtest
  const handleRunBacktest = async () => {
    if (!buySignal) {
      message.error(t('strategyBuilder.selectBuySignal', 'Please select a buy signal'));
      return;
    }

    // Need at least one exit condition
    const hasTakeProfit = enableTakeProfit && takeProfitPct;
    const hasStopLoss = enableStopLoss && stopLossPct;
    const hasHoldingLimit = enableMaxHolding && maxHoldingHours && maxHoldingHours > 0;
    
    if (sellRuleType === 'fixed_rule' && !hasTakeProfit && !hasStopLoss && !hasHoldingLimit) {
      message.error(t('strategyBuilder.setExitRule', 'Please set at least one exit condition'));
      return;
    }

    if (sellRuleType === 'signal' && !sellSignal) {
      message.error(t('strategyBuilder.selectSellSignal', 'Please select a sell signal'));
      return;
    }

    setIsLoading(true);
    try {
      console.log('[StrategyBuilder] Starting backtest...');
      const sellRule = buildSellRule();
      console.log('[StrategyBuilder] Sell rule:', sellRule);
      
      const results = await runBacktest({
        buy_signal_id: buySignal.id,
        sell_rule: sellRule,
        symbol: asset,
        timeframe: timeframe,
        trading_mode: tradingMode,
        futures_settings: tradingMode === 'futures' ? {
          leverage: leverage,
          direction: positionDirection
        } : undefined
      });
      
      console.log('[StrategyBuilder] Backtest results:', results);
      
      if (results) {
        setBacktestResults(results);
        setViewMode('performance');
        message.success(t('strategyBuilder.backtestComplete', 'Backtest completed'));
      } else {
        console.error('[StrategyBuilder] No results returned');
        message.error('Backtest returned no results');
      }
    } catch (error: any) {
      console.error('[StrategyBuilder] Backtest error:', error);
      message.error(error.message || t('strategyBuilder.backtestFailed', 'Backtest failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Open save modal
  const handleOpenSaveModal = () => {
    if (!buySignal) {
      message.error(t('strategyBuilder.selectBuySignal', 'Please select a buy signal'));
      return;
    }
    if (!backtestResults) {
      message.error(t('strategyBuilder.runBacktestFirst', 'Please run backtest first'));
      return;
    }
    // Generate default name from signal name
    setSaveStrategyName(buySignal.name ? `${buySignal.name} Strategy` : '');
    setSaveStrategyDescription('');
    setShowSaveModal(true);
  };

  // Save strategy
  const handleSaveStrategy = async () => {
    if (!saveStrategyName.trim()) {
      message.error(t('strategyBuilder.enterName', 'Please enter a strategy name'));
      return;
    }

    if (!buySignal) {
      message.error(t('strategyBuilder.selectBuySignal', 'Please select a buy signal'));
      return;
    }

    setIsLoading(true);
    try {
      await saveStrategy({
        name: saveStrategyName.trim(),
        description: saveStrategyDescription.trim(),
        buy_signal_id: buySignal.id,
        sell_rule: buildSellRule(),
        symbol: asset,
        timeframe: timeframe,
        backtest_results: backtestResults || undefined,
        trading_mode: tradingMode,
        futures_settings: tradingMode === 'futures' ? {
          leverage: leverage,
          direction: positionDirection
        } : undefined
      });
      message.success(t('strategyBuilder.saveSuccess', 'Strategy saved successfully'));
      setShowSaveModal(false);
      handleBackToBuild();
      // Reset form after save
      setSaveStrategyName('');
      setSaveStrategyDescription('');
      setBacktestResults(null);
      setBuySignal(null);
      onSaveComplete?.();
    } catch (error: any) {
      message.error(error.message || t('strategyBuilder.saveFailed', 'Failed to save strategy'));
    } finally {
      setIsLoading(false);
    }
  };

  // Signal display card - New UIUX Design
  const SignalCard: React.FC<{
    signal: SavedSignal;
    role: 'buy' | 'sell';
    onRemove: () => void;
  }> = ({ signal, role, onRemove }) => (
    <Card
      size="small"
      style={{
        background: theme.toolbarBackground,
        borderRadius: 16,
        marginBottom: 6,
        borderLeft: `3px solid ${role === 'buy' ? theme.candleUp : theme.candleDown}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
      }}
      styles={{ body: { padding: '10px 12px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Left icon */}
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: role === 'buy' 
            ? 'linear-gradient(135deg, #e6f7e6 0%, #d4f0d4 100%)' 
            : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <span style={{ 
            fontSize: 12, 
            color: role === 'buy' ? theme.candleUp : theme.candleDown,
            fontWeight: 'bold'
          }}>
            {role === 'buy' ? '↗' : '↘'}
          </span>
        </div>
        
        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <div style={{ fontWeight: 600, fontSize: 12, color: theme.textPrimary, marginBottom: 3 }}>
            {signal.name}
          </div>
          
          {/* Tags */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
            <Tag style={{ 
              background: '#e6f4ff', 
              color: '#1677ff', 
              border: 'none',
              borderRadius: 3,
              margin: 0,
              fontSize: 10,
              padding: '0 6px',
              lineHeight: '18px'
            }}>
              {signal.symbol}
            </Tag>
            <Tag style={{ 
              background: '#f5f5f5', 
              color: '#666', 
              border: 'none',
              borderRadius: 3,
              margin: 0,
              fontSize: 10,
              padding: '0 6px',
              lineHeight: '18px'
            }}>
              {signal.timeframe}
            </Tag>
            <Tag style={{ 
              background: signal.signal_type === 'point' ? '#f9f0ff' : '#f0f5ff', 
              color: signal.signal_type === 'point' ? '#722ed1' : '#2f54eb', 
              border: 'none',
              borderRadius: 3,
              margin: 0,
              fontSize: 10,
              padding: '0 6px',
              lineHeight: '18px'
            }}>
              {signal.signal_type === 'point' ? 'Point' : 'Rectangle'}
            </Tag>
          </div>
          
          {/* Time range */}
          <div style={{ fontSize: 11, color: theme.textSecondary }}>
            {formatTimestamp(signal.time_range.start_time)}–{formatTimestamp(signal.time_range.end_time)}
          </div>
        </div>
        
        {/* Delete button */}
        <Tooltip title={t('common.delete', 'Delete')}>
          <Button 
            type="text" 
            size="small" 
            icon={<DeleteOutlined style={{ fontSize: 14, color: theme.textSecondary }} />}
            onClick={onRemove}
            style={{ padding: 4 }}
          />
        </Tooltip>
      </div>
    </Card>
  );

  // Add signal button
  const AddSignalButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Button
      type="dashed"
      block
      onClick={onClick}
      style={{
        borderColor: theme.accentPrimary,
        color: theme.accentPrimary,
        borderRadius: 6,
        height: 34,
        fontSize: 12
      }}
      icon={<PlusOutlined />}
    >
      {t('strategyBuilder.addSignal', 'Add Signal')}
    </Button>
  );

  // Performance View - shown after backtest (matches UIUX design exactly)
  const PerformanceView = () => {
    if (!backtestResults) return null;
    
    // Get validation split data
    const validationSplit = backtestResults.validation_split;
    const hasSplit = validationSplit && validationSplit.before && validationSplit.after;
    
    // Get current data based on split view mode
    const getCurrentData = () => {
      if (!hasSplit || splitViewMode === 'all') {
        return backtestResults;
      }
      if (splitViewMode === 'before') {
        return validationSplit.before;
      }
      if (splitViewMode === 'after') {
        return validationSplit.after;
      }
      return backtestResults;
    };
    
    const currentData = getCurrentData();
    
    // Backend already returns win_rate as percentage (e.g., 68 means 68%)
    const winRate = currentData.win_rate ?? 0;
    const sharpeRatio = currentData.sharpe_ratio ?? 0;
    const maxDrawdown = currentData.max_drawdown ?? 0;
    const totalReturn = currentData.total_return ?? 0;
    const totalTrades = currentData.total_trades ?? 0;
    const profitFactor = currentData.profit_factor ?? 0;
    const trades = currentData.trades ?? [];
    const equityCurve = currentData.equity_curve ?? [];
    
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: theme.surfaceBackground
      }}>
        {/* Header */}
        <div style={{ 
          padding: '12px 16px', 
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined style={{ fontSize: 12 }} />} 
            onClick={handleBackToBuild}
            style={{ color: theme.textPrimary, padding: 4, minWidth: 'auto' }}
          />
          <Text strong style={{ fontSize: 13, color: theme.textPrimary, fontWeight: 600 }}>
            {t('strategyBuilder.strategyPerformance', 'Strategy Performance')}
          </Text>
          
          {/* Before/After Split Toggle */}
          {hasSplit && (
            <Segmented
              size="small"
              value={splitViewMode}
              onChange={(value) => setSplitViewMode(value as 'all' | 'before' | 'after')}
              options={[
                {
                  label: (
                    <span style={{ fontSize: 11, padding: '0 4px' }}>
                      All <Tag style={{ margin: '0 0 0 4px', fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>{backtestResults.total_trades}</Tag>
                    </span>
                  ),
                  value: 'all'
                },
                {
                  label: (
                    <span style={{ fontSize: 11, padding: '0 4px' }}>
                      {t('analysis.split.before', 'Before')} <Tag color="blue" style={{ margin: '0 0 0 4px', fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>{validationSplit?.before?.total_trades ?? 0}</Tag>
                    </span>
                  ),
                  value: 'before'
                },
                {
                  label: (
                    <span style={{ fontSize: 11, padding: '0 4px' }}>
                      {t('analysis.split.after', 'After')} <Tag color="green" style={{ margin: '0 0 0 4px', fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>{validationSplit?.after?.total_trades ?? 0}</Tag>
                    </span>
                  ),
                  value: 'after'
                }
              ]}
              style={{ 
                marginLeft: 'auto',
                background: theme.toolbarBackground
              }}
            />
          )}
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '0 16px 16px'
        }}>
          {/* Performance Section */}
          <div style={{ marginBottom: 16 }}>
            <Text style={{ 
              color: theme.textSecondary, 
              fontSize: 11, 
              fontWeight: 600,
              letterSpacing: 0.5,
              display: 'block',
              marginBottom: 8
            }}>
              PERFORMANCE
            </Text>
            
            {/* Metrics Card */}
            <div style={{ 
              background: theme.toolbarBackground, 
              borderRadius: 10, 
              padding: '14px 12px',
              border: `1px solid ${theme.surfaceBorder}`
            }}>
              {/* Row 1 */}
              <div style={{ display: 'flex', marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    WIN RATE
                  </Text>
                  <Text style={{ color: theme.candleUp, fontSize: 18, fontWeight: 600 }}>
                    {winRate.toFixed(0)}%
                  </Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    SHARPE APP.
                  </Text>
                  <Text style={{ color: theme.textPrimary, fontSize: 18, fontWeight: 600 }}>
                    {sharpeRatio.toFixed(1)}
                  </Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    MAX DRAW.
                  </Text>
                  <Text style={{ color: theme.candleDown, fontSize: 18, fontWeight: 600 }}>
                    -{Math.abs(maxDrawdown).toFixed(1)}%
                  </Text>
                </div>
              </div>
              
              {/* Row 2 */}
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    TOTAL RETURN
                  </Text>
                  <Text style={{ 
                    color: totalReturn >= 0 ? theme.candleUp : theme.candleDown, 
                    fontSize: 18, 
                    fontWeight: 600 
                  }}>
                    {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
                  </Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    TOTAL TRADES
                  </Text>
                  <Text style={{ color: theme.textPrimary, fontSize: 18, fontWeight: 600 }}>
                    {totalTrades}
                  </Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 10, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                    PROFIT FACTOR
                  </Text>
                  <Text style={{ color: theme.textPrimary, fontSize: 18, fontWeight: 600 }}>
                    {profitFactor.toFixed(1)}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {/* Equity Curve & Trade History */}
          {equityCurve && equityCurve.length > 1 && (
            <div style={{ marginTop: 16 }}>
              <Text style={{ 
                color: theme.textSecondary, 
                fontSize: 11, 
                fontWeight: 600,
                letterSpacing: 0.5,
                display: 'block',
                marginBottom: 8
              }}>
                {t('strategyBuilder.equityCurve', 'EQUITY CURVE')}
              </Text>
              
              <div style={{ 
                background: theme.toolbarBackground, 
                borderRadius: 10, 
                padding: '12px',
                border: `1px solid ${theme.surfaceBorder}`
              }}>
                {/* Mini Equity Curve using SVG */}
                <svg 
                  width="100%" 
                  height="50" 
                  viewBox={`0 0 ${equityCurve.length} 50`}
                  preserveAspectRatio="none"
                  style={{ display: 'block' }}
                >
                  {(() => {
                    const data = equityCurve;
                    const values = data.map(d => d.equity);
                    const minVal = Math.min(...values);
                    const maxVal = Math.max(...values);
                    const range = maxVal - minVal || 1;
                    const startVal = values[0];
                    const endVal = values[values.length - 1];
                    const isPositive = endVal >= startVal;
                    
                    const points = data.map((d, i) => {
                      const x = i;
                      const y = 45 - ((d.equity - minVal) / range) * 40;
                      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                    }).join(' ');
                    
                    const areaPath = `${points} L ${data.length - 1} 45 L 0 45 Z`;
                    
                    return (
                      <>
                        <path d={areaPath} fill={isPositive ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 77, 79, 0.1)'} />
                        <path d={points} fill="none" stroke={isPositive ? '#52c41a' : '#ff4d4f'} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                      </>
                    );
                  })()}
                </svg>
                
                {/* Stats row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: theme.textSecondary }}>
                  <span>{t('strategyBuilder.trades', 'Trades')}: {totalTrades}</span>
                  <span style={{ color: totalReturn >= 0 ? theme.candleUp : theme.candleDown, fontWeight: 500 }}>
                    {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
                  </span>
                </div>
              </div>
              
              {/* Trade History List */}
              {trades && trades.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
                    {t('strategyBuilder.tradeHistory', 'TRADE HISTORY')}
                  </Text>
                  <div style={{ 
                    background: theme.toolbarBackground, 
                    borderRadius: 10, 
                    border: `1px solid ${theme.surfaceBorder}`,
                    maxHeight: 150,
                    overflowY: 'auto'
                  }}>
                    {trades.map((trade, i) => {
                      // Format entry time as short date: "12/16/2024"
                      const entryDate = new Date(trade.entry_time);
                      const shortDate = `${entryDate.getUTCMonth() + 1}/${entryDate.getUTCDate()}/${entryDate.getUTCFullYear()}`;
                      
                      return (
                        <div 
                          key={i}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 12px',
                            borderBottom: i < trades.length - 1 ? `1px solid ${theme.surfaceBorder}` : 'none',
                            fontSize: 12
                          }}
                        >
                          <span style={{ color: theme.textSecondary, width: 24 }}>#{i + 1}</span>
                          <span style={{ color: theme.textPrimary, width: 72 }}>{shortDate}</span>
                          <span style={{ 
                            color: (trade.return_pct ?? 0) >= 0 ? theme.candleUp : theme.candleDown,
                            fontWeight: 500,
                            width: 55,
                            textAlign: 'right'
                          }}>
                            {(trade.return_pct ?? 0) >= 0 ? '+' : ''}{(trade.return_pct ?? 0).toFixed(2)}%
                          </span>
                          <Tag 
                            color={trade.exit_reason === 'take_profit' ? 'green' : trade.exit_reason === 'stop_loss' ? 'red' : 'default'} 
                            style={{ fontSize: 10, margin: '0 6px', padding: '0 4px' }}
                          >
                            {trade.exit_reason === 'take_profit' ? 'TP' : trade.exit_reason === 'stop_loss' ? 'SL' : trade.exit_reason === 'max_holding' ? 'TIME' : 'END'}
                          </Tag>
                          <Button 
                            type="link" 
                            size="small" 
                            icon={<EyeOutlined />}
                            onClick={() => handleViewTrade(trade)}
                            style={{ padding: 0, height: 'auto', fontSize: 11 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Chat Section */}
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 16 }}>
            <Text style={{ 
              color: theme.textSecondary, 
              fontSize: 11, 
              fontWeight: 600,
              letterSpacing: 0.5,
              display: 'block',
              marginBottom: 8
            }}>
              AI ANALYSIS
            </Text>
            
            {/* Chat Container (Messages + Input) */}
            <div style={{ 
              background: theme.toolbarBackground, 
              borderRadius: 10, 
              border: `1px solid ${theme.surfaceBorder}`,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 280,
              maxHeight: 450
            }}>
              {/* Messages Area */}
              <div 
                ref={analysisContainerRef}
                style={{ 
                  flex: 1,
                  padding: '12px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12
                }}
              >
                {/* Chat History */}
                {chatHistory.map((msg, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <div style={{
                      maxWidth: '85%',
                      padding: '8px 12px',
                      borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      background: msg.role === 'user' ? '#1890ff' : theme.surfaceBackground,
                      color: msg.role === 'user' ? '#fff' : theme.textPrimary,
                      fontSize: 13,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      boxShadow: msg.role === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                
                {/* Current streaming response */}
                {aiAnalysis && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      maxWidth: '85%',
                      padding: '8px 12px',
                      borderRadius: '12px 12px 12px 4px',
                      background: theme.surfaceBackground,
                      color: theme.textPrimary,
                      fontSize: 13,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {aiAnalysis}
                      {isStreaming && (
                        <span style={{ 
                          display: 'inline-block', 
                          width: 6, 
                          height: 14, 
                          background: '#1890ff', 
                          marginLeft: 2,
                          animation: 'blink 1s infinite',
                          verticalAlign: 'middle'
                        }} />
                      )}
                    </div>
                  </div>
                )}
                
                {/* Initial loading state */}
                {isAnalyzing && !aiAnalysis && chatHistory.length === 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      maxWidth: '85%',
                      padding: '8px 12px',
                      borderRadius: '12px 12px 12px 4px',
                      background: theme.surfaceBackground,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 4 }} />
                      <Skeleton.Input active size="small" style={{ width: 150, marginBottom: 4 }} />
                      <Skeleton.Input active size="small" style={{ width: 180 }} />
                    </div>
                  </div>
                )}
                
                {/* Empty state */}
                {!isAnalyzing && chatHistory.length === 0 && !aiAnalysis && (
                  <Text style={{ color: theme.textSecondary, fontSize: 13, textAlign: 'center', padding: 20 }}>
                    {t('strategyBuilder.waitingForAnalysis', 'Waiting for AI analysis...')}
                  </Text>
                )}
              </div>
              
              {/* Input Area - Inside the chat container */}
              <div style={{ 
                borderTop: `1px solid ${theme.surfaceBorder}`,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: theme.surfaceBackground,
                borderRadius: '0 0 10px 10px'
              }}>
                <Input
                  ref={followUpInputRef}
                  placeholder={t('strategyBuilder.askFollowUp', 'Ask a follow-up...')}
                  bordered={false}
                  value={followUpInput}
                  onChange={(e) => setFollowUpInput(e.target.value)}
                  onPressEnter={handleFollowUp}
                  disabled={isAnalyzing}
                  style={{ flex: 1, background: 'transparent', fontSize: 13 }}
                />
                <Button
                  type="text"
                  icon={isAnalyzing ? <LoadingOutlined /> : <SendOutlined />}
                  onClick={handleFollowUp}
                  disabled={!followUpInput.trim() || isAnalyzing}
                  style={{ 
                    color: followUpInput.trim() && !isAnalyzing ? '#1890ff' : theme.textSecondary,
                    padding: 4
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{ 
          padding: '12px 16px', 
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <Button
            type="primary"
            block
            onClick={handleOpenSaveModal}
            loading={isLoading}
            style={{
              borderRadius: 6,
              height: 36,
              background: theme.candleUp,
              border: 'none',
              fontWeight: 500,
              fontSize: 12,
              color: '#fff'
            }}
          >
            {t('strategyBuilder.saveStrategy', 'Save Strategy')}
          </Button>
          
          <Button
            block
            onClick={handleBackToBuild}
            style={{
              borderRadius: 6,
              height: 36,
              background: 'transparent',
              border: `1px solid ${theme.surfaceBorder}`,
              fontWeight: 500,
              fontSize: 12,
              color: theme.textPrimary
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
        
        {/* CSS for blinking cursor */}
        <style>{`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}</style>
      </div>
    );
  };

  // Render Performance View if in performance mode
  if (viewMode === 'performance' && backtestResults) {
    return (
      <>
        {PerformanceView()}
        
        {/* Save Strategy Modal */}
        <Modal
          title={t('strategyBuilder.saveStrategy', 'Save Strategy')}
          open={showSaveModal}
          onCancel={() => setShowSaveModal(false)}
          onOk={handleSaveStrategy}
          okText={t('common.save', 'Save')}
          cancelText={t('common.cancel', 'Cancel')}
          confirmLoading={isLoading}
          styles={{
            content: {
              background: theme.surfaceBackground
            }
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 0' }}>
            <div>
              <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
                {t('strategyBuilder.strategyName', 'STRATEGY NAME')} *
              </Text>
              <Input
                placeholder={t('strategyBuilder.namePlaceholder', 'Enter strategy name')}
                value={saveStrategyName}
                onChange={(e) => setSaveStrategyName(e.target.value)}
                style={{
                  background: theme.toolbarBackground,
                  borderColor: theme.surfaceBorder,
                  borderRadius: 8
                }}
                autoFocus
              />
            </div>
            <div>
              <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
                {t('strategyBuilder.description', 'DESCRIPTION')} ({t('common.optional', 'Optional')})
              </Text>
              <Input.TextArea
                placeholder={t('strategyBuilder.descriptionPlaceholder', 'Describe this strategy...')}
                value={saveStrategyDescription}
                onChange={(e) => setSaveStrategyDescription(e.target.value)}
                rows={3}
                style={{
                  background: theme.toolbarBackground,
                  borderColor: theme.surfaceBorder,
                  borderRadius: 8
                }}
              />
            </div>
          </div>
        </Modal>
        
        {/* Trade Chart Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {t('strategyBuilder.tradeDetails', 'Trade Details')}
              {selectedTrade && (
                <Tag color={(selectedTrade.return_pct ?? 0) >= 0 ? 'green' : 'red'}>
                  {(selectedTrade.return_pct ?? 0) >= 0 ? '+' : ''}{(selectedTrade.return_pct ?? 0).toFixed(2)}%
                </Tag>
              )}
            </div>
          }
          open={showTradeChartModal}
          onCancel={() => {
            setShowTradeChartModal(false);
            setSelectedTrade(null);
            setTradeChartData([]);
          }}
          footer={null}
          width={800}
          styles={{
            content: {
              background: theme.surfaceBackground
            }
          }}
        >
          {tradeChartLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : selectedTrade && (
            <div style={{ padding: '8px 0' }}>
              {/* Trade Info */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)', 
                gap: 12, 
                marginBottom: 16,
                padding: '12px',
                background: theme.toolbarBackground,
                borderRadius: 8
              }}>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.entryTime', 'Entry Time')}
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    {new Date(selectedTrade.entry_time).toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} UTC
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.exitTime', 'Exit Time')}
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    {new Date(selectedTrade.exit_time).toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} UTC
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.holdingPeriod', 'Hold Time')}
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    {selectedTrade.holding_period_hours?.toFixed(1)}h
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.exitReason', 'Exit Reason')}
                  </Text>
                  <Tag color={
                    selectedTrade.exit_reason === 'take_profit' ? 'green' :
                    selectedTrade.exit_reason === 'stop_loss' ? 'red' :
                    selectedTrade.exit_reason === 'max_holding' ? 'orange' : 'default'
                  } style={{ fontSize: 11, margin: 0 }}>
                    {selectedTrade.exit_reason === 'take_profit' ? 'Take Profit' :
                     selectedTrade.exit_reason === 'stop_loss' ? 'Stop Loss' :
                     selectedTrade.exit_reason === 'max_holding' ? 'Max Holding' : 'End of Data'}
                  </Tag>
                </div>
              </div>
              
              {/* Price Info */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 12, 
                marginBottom: 16,
                padding: '12px',
                background: theme.toolbarBackground,
                borderRadius: 8
              }}>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.entryPrice', 'Entry Price')}
                  </Text>
                  <Text strong style={{ fontSize: 14, color: '#1890ff' }}>
                    ${selectedTrade.entry_price?.toLocaleString()}
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 11, display: 'block' }}>
                    {t('strategyBuilder.exitPrice', 'Exit Price')}
                  </Text>
                  <Text strong style={{ fontSize: 14, color: (selectedTrade.return_pct ?? 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>
                    ${selectedTrade.exit_price?.toLocaleString()}
                  </Text>
                </div>
              </div>
              
              {/* Chart */}
              {tradeChartData.length > 0 ? (
                <div 
                  ref={tradeChartContainerRef}
                  style={{ 
                    height: 350, 
                    background: '#1a1a2e',
                    borderRadius: 8,
                    overflow: 'hidden'
                  }}
                >
                  <TradeChartComponent 
                    data={tradeChartData}
                    entryTime={Math.floor(selectedTrade.entry_time / 1000)}
                    exitTime={Math.floor(selectedTrade.exit_time / 1000)}
                    entryPrice={selectedTrade.entry_price}
                    exitPrice={selectedTrade.exit_price}
                    theme={theme}
                  />
                </div>
              ) : (
                <div style={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  background: theme.toolbarBackground,
                  borderRadius: 8,
                  color: theme.textSecondary
                }}>
                  {t('strategyBuilder.noChartData', 'No chart data available for this period')}
                </div>
              )}
            </div>
          )}
        </Modal>
      </>
    );
  }

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: theme.surfaceBackground
    }}>
      {/* Header */}
      {showBackButton && (
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: `1px solid ${theme.surfaceBorder}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined style={{ fontSize: 12 }} />} 
            onClick={onBack}
            style={{ color: theme.textSecondary, padding: 4 }}
          />
          <Text strong style={{ margin: 0, color: theme.textPrimary, fontSize: 13 }}>
            {editingStrategy 
              ? t('strategyBuilder.editStrategy', 'Edit Strategy')
              : t('strategyBuilder.buildStrategy', 'Build Strategy')
            }
          </Text>
        </div>
      )}

      {/* Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }}>
        {/* Asset & Timeframe */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
              {t('strategyBuilder.asset', 'ASSET')}
            </Text>
            <Select
              value={asset}
              onChange={setAsset}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              options={[
                // Crypto
                { value: 'BTCUSDT', label: 'BTC (Bitcoin)' },
                { value: 'ETHUSDT', label: 'ETH (Ethereum)' },
                { value: 'BNBUSDT', label: 'BNB' },
                { value: 'SOLUSDT', label: 'SOL (Solana)' },
                { value: 'XRPUSDT', label: 'XRP' },
                // Stocks
                { value: 'AAPL', label: 'AAPL (Apple)' },
                { value: 'GOOGL', label: 'GOOGL (Google)' },
                { value: 'MSFT', label: 'MSFT (Microsoft)' },
                { value: 'TSLA', label: 'TSLA (Tesla)' },
                { value: 'NVDA', label: 'NVDA (Nvidia)' },
                { value: 'AMZN', label: 'AMZN (Amazon)' },
                { value: 'META', label: 'META (Meta)' },
                // Commodities
                { value: 'GC=F', label: 'GC=F (Gold)' }
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
              {t('strategyBuilder.timeframe', 'TIMEFRAME')}
            </Text>
            <Select
              value={timeframe}
              onChange={setTimeframe}
              style={{ width: '100%' }}
              options={[
                { value: '1m', label: '1m' },
                { value: '5m', label: '5m' },
                { value: '30m', label: '30m' },
                { value: '1h', label: '1h' },
                { value: '4h', label: '4h' },
                { value: '1d', label: '1D' }
              ]}
            />
          </div>
        </div>

        {/* Trading Mode Selection */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            marginBottom: 8 
          }}>
            <ThunderboltOutlined style={{ color: theme.textSecondary, fontSize: 12 }} />
            <Text strong style={{ color: theme.textPrimary, fontSize: 12 }}>
              {t('strategyBuilder.tradingMode', 'TRADING MODE')}
            </Text>
          </div>
          
          <Segmented
            value={tradingMode}
            onChange={(value) => {
              setTradingMode(value as 'spot' | 'futures');
              if (value === 'spot') {
                setLeverage(1);
                setPositionDirection('long');
              }
            }}
            options={[
              { label: t('strategyBuilder.spot', 'Spot'), value: 'spot' },
              { label: t('strategyBuilder.futures', 'Futures'), value: 'futures' }
            ]}
            block
            style={{ marginBottom: 0 }}
          />
          
          {/* Futures Settings */}
          {tradingMode === 'futures' && (
            <div style={{ 
              background: theme.surfaceBackground, 
              borderRadius: 8, 
              padding: 10,
              marginTop: 8,
              border: `1px solid ${theme.surfaceBorder}`
            }}>
              {/* Direction */}
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 10, color: theme.textSecondary, marginBottom: 4, display: 'block' }}>
                  {t('strategyBuilder.direction', 'Direction')}
                </Text>
                <Segmented
                  value={positionDirection}
                  onChange={(value) => setPositionDirection(value as 'long' | 'short')}
                  options={[
                    { label: t('strategyBuilder.long', 'Long'), value: 'long' },
                    { label: t('strategyBuilder.short', 'Short'), value: 'short' }
                  ]}
                  block
                  size="small"
                />
              </div>
              
              {/* Leverage */}
              <div>
                <Text style={{ fontSize: 10, color: theme.textSecondary, marginBottom: 4, display: 'block' }}>
                  {t('strategyBuilder.leverage', 'Leverage')}: {leverage}x
                </Text>
                <Select
                  value={leverage}
                  onChange={setLeverage}
                  style={{ width: '100%' }}
                  size="small"
                  options={[
                    { label: '1x', value: 1 },
                    { label: '2x', value: 2 },
                    { label: '3x', value: 3 },
                    { label: '5x', value: 5 },
                    { label: '10x', value: 10 },
                    { label: '20x', value: 20 },
                    { label: '50x', value: 50 },
                    { label: '100x', value: 100 },
                    { label: '125x', value: 125 }
                  ]}
                />
              </div>
              
              {/* Risk Warning */}
              {leverage >= 10 && (
                <div style={{ 
                  marginTop: 8, 
                  padding: 6, 
                  background: 'rgba(255, 77, 79, 0.1)', 
                  borderRadius: 4,
                  fontSize: 10,
                  color: theme.candleDown
                }}>
                  ⚠️ {leverage}x: {t('strategyBuilder.leverageWarning', { pct: (100/leverage).toFixed(1) })}
                </div>
              )}
            </div>
          )}
        </div>

        <Divider style={{ margin: '4px 0', borderColor: theme.surfaceBorder }} />

        {/* Buy Conditions */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            marginBottom: 10 
          }}>
            <FilterOutlined style={{ color: theme.candleUp, fontSize: 12 }} />
            <Text strong style={{ color: theme.textPrimary, fontSize: 12 }}>
              {t('strategyBuilder.buyConditions', 'BUY CONDITIONS')}
            </Text>
          </div>
          
          {buySignal ? (
            <SignalCard 
              signal={buySignal} 
              role="buy" 
              onRemove={() => setBuySignal(null)} 
            />
          ) : null}
          
          {!buySignal && (
            <AddSignalButton onClick={() => setShowBuySignalModal(true)} />
          )}
        </div>

        <Divider style={{ margin: '4px 0', borderColor: theme.surfaceBorder }} />

        {/* Sell Conditions */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            marginBottom: 10 
          }}>
            <FilterOutlined style={{ color: theme.candleDown, fontSize: 12 }} />
            <Text strong style={{ color: theme.textPrimary, fontSize: 12 }}>
              {t('strategyBuilder.sellConditions', 'SELL CONDITIONS')}
            </Text>
          </div>

          {/* Sell Rule Type Toggle */}
          <div style={{ 
            marginBottom: 12,
            padding: '8px 12px',
            background: theme.toolbarBackground,
            borderRadius: 8,
            border: `1px solid ${theme.surfaceBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
              {t('strategyBuilder.useSignalExit', 'Use sell signal')}
            </Text>
            <Switch
              size="small"
              checked={sellRuleType === 'signal'}
              onChange={(checked) => setSellRuleType(checked ? 'signal' : 'fixed_rule')}
            />
          </div>

          {/* Sell Signal Selection (when using signal) */}
          {sellRuleType === 'signal' && (
            <div style={{ marginBottom: 12 }}>
              {sellSignal ? (
                <SignalCard 
                  signal={sellSignal} 
                  role="sell" 
                  onRemove={() => setSellSignal(null)} 
                />
              ) : (
                <AddSignalButton onClick={() => setShowSellSignalModal(true)} />
              )}
            </div>
          )}

          {/* Take Profit */}
          <div style={{ 
            padding: 12,
            background: theme.toolbarBackground,
            borderRadius: 8,
            marginBottom: 8,
            border: `1px solid ${theme.surfaceBorder}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: enableTakeProfit ? 8 : 0 }}>
              <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
                {t('strategyBuilder.takeProfit', 'Take Profit')}
              </Text>
              <Switch 
                size="small" 
                checked={enableTakeProfit} 
                onChange={setEnableTakeProfit}
              />
            </div>
            {enableTakeProfit && (
              <InputNumber
                value={takeProfitPct}
                onChange={(v) => setTakeProfitPct(v)}
                min={0.1}
                max={100}
                step={0.5}
                placeholder="5"
                style={{ width: '100%' }}
                addonAfter="%"
              />
            )}
          </div>

          {/* Stop Loss */}
          <div style={{ 
            padding: 12,
            background: theme.toolbarBackground,
            borderRadius: 8,
            marginBottom: 8,
            border: `1px solid ${theme.surfaceBorder}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: enableStopLoss ? 8 : 0 }}>
              <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
                {t('strategyBuilder.stopLoss', 'Stop Loss')}
              </Text>
              <Switch 
                size="small" 
                checked={enableStopLoss} 
                onChange={setEnableStopLoss}
              />
            </div>
            {enableStopLoss && (
              <InputNumber
                value={stopLossPct}
                onChange={(v) => setStopLossPct(v)}
                min={0.1}
                max={100}
                step={0.5}
                placeholder="2"
                style={{ width: '100%' }}
                addonAfter="%"
              />
            )}
          </div>

          {/* Max Holding Period */}
          <div style={{ 
            padding: 12,
            background: theme.toolbarBackground,
            borderRadius: 8,
            border: `1px solid ${theme.surfaceBorder}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: enableMaxHolding ? 8 : 0 }}>
              <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
                {t('strategyBuilder.maxHoldingPeriod', 'Max Holding Period')}
              </Text>
              <Switch 
                size="small" 
                checked={enableMaxHolding} 
                onChange={setEnableMaxHolding}
              />
            </div>
            {enableMaxHolding && (
              <>
                <InputNumber
                  value={maxHoldingHours}
                  onChange={(v) => setMaxHoldingHours(v)}
                  min={1}
                  max={8760}
                  step={1}
                  placeholder="168"
                  style={{ width: '100%' }}
                  addonAfter={t('strategyBuilder.hours', 'hours')}
                />
                <Text style={{ color: theme.textSecondary, fontSize: 10, marginTop: 4, display: 'block' }}>
                  {maxHoldingHours ? `≈ ${(maxHoldingHours / 24).toFixed(1)} ${t('strategyBuilder.days', 'days')}` : ''}
                </Text>
              </>
            )}
          </div>

          {sellRuleType === 'signal' && (
            <Text style={{ color: theme.textSecondary, fontSize: 11, marginTop: 8, display: 'block', padding: '0 4px' }}>
              {t('strategyBuilder.tpslBackup', 'TP/SL acts as backup exit when using signal')}
            </Text>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ 
        padding: '12px 16px', 
        borderTop: `1px solid ${theme.surfaceBorder}`
      }}>
        <Button
          type="primary"
          block
          icon={<PlayCircleOutlined />}
          onClick={handleRunBacktest}
          loading={isLoading}
          style={{
            borderRadius: 6,
            height: 36,
            background: theme.candleUp,
            border: 'none',
            fontWeight: 500,
            fontSize: 13
          }}
        >
          {t('strategyBuilder.buildStrategy', 'Build Strategy')}
        </Button>
      </div>

      {/* Signal Selector Modals */}
      <SignalSelectorModal
        open={showBuySignalModal}
        onClose={() => setShowBuySignalModal(false)}
        onSelect={setBuySignal}
        title={t('strategyBuilder.selectBuySignal', 'Select Buy Signal')}
        excludeSignalIds={sellSignal ? [sellSignal.id] : []}
        // 移除filterSymbol和filterTimeframe，允许用户选择任何交易对和时间周期的信号
        // 用户可以通过搜索功能来过滤信号
      />
      
      <SignalSelectorModal
        open={showSellSignalModal}
        onClose={() => setShowSellSignalModal(false)}
        onSelect={setSellSignal}
        title={t('strategyBuilder.selectSellSignal', 'Select Sell Signal')}
        excludeSignalIds={buySignal ? [buySignal.id] : []}
        // 移除filterSymbol和filterTimeframe，允许用户选择任何交易对和时间周期的信号
        // 用户可以通过搜索功能来过滤信号
      />

      {/* Save Strategy Modal */}
      <Modal
        title={t('strategyBuilder.saveStrategy', 'Save Strategy')}
        open={showSaveModal}
        onCancel={() => setShowSaveModal(false)}
        onOk={handleSaveStrategy}
        okText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
        confirmLoading={isLoading}
        styles={{
          content: {
            background: theme.surfaceBackground
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 0' }}>
          <div>
            <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
              {t('strategyBuilder.strategyName', 'STRATEGY NAME')} *
            </Text>
            <Input
              placeholder={t('strategyBuilder.namePlaceholder', 'Enter strategy name')}
              value={saveStrategyName}
              onChange={(e) => setSaveStrategyName(e.target.value)}
              style={{
                background: theme.toolbarBackground,
                borderColor: theme.surfaceBorder,
                borderRadius: 8
              }}
              autoFocus
            />
          </div>
          <div>
            <Text strong style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'block' }}>
              {t('strategyBuilder.description', 'DESCRIPTION')} ({t('common.optional', 'Optional')})
            </Text>
            <Input.TextArea
              placeholder={t('strategyBuilder.descriptionPlaceholder', 'Describe this strategy...')}
              value={saveStrategyDescription}
              onChange={(e) => setSaveStrategyDescription(e.target.value)}
              rows={3}
              style={{
                background: theme.toolbarBackground,
                borderColor: theme.surfaceBorder,
                borderRadius: 8
              }}
            />
          </div>
          {/* Quick summary */}
          {buySignal && (
            <div style={{ 
              padding: 12, 
              background: theme.toolbarBackground, 
              borderRadius: 8,
              border: `1px solid ${theme.surfaceBorder}`
            }}>
              <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
                {t('strategyBuilder.strategySummary', 'Strategy Summary')}:
              </Text>
              <div style={{ marginTop: 8, color: theme.textPrimary, fontSize: 13 }}>
                <div>📈 {t('strategyBuilder.buySignal', 'Buy Signal')}: {buySignal.name}</div>
                <div>🎯 {asset} / {timeframe}</div>
                {enableTakeProfit && takeProfitPct && <div>✅ TP: {takeProfitPct}%</div>}
                {enableStopLoss && stopLossPct && <div>🛑 SL: {stopLossPct}%</div>}
                {enableMaxHolding && maxHoldingHours && <div>⏱️ Max Hold: {maxHoldingHours}h</div>}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default StrategyBuilder;

